/**
 * Follow-up Action Functions - Fetch and mutation operations for Follow-ups
 *
 * These functions handle all follow-up fetch and mutation operations.
 * Uses validated fetch with Zod schemas for runtime type safety.
 *
 * Two patterns coexist:
 * - Function-style (fetchWorkspaceFollowUps, etc.) for dashboard/lists
 * - Atom-style (loadFollowUpDetailAtom, etc.) for detail page
 */

import { atom } from "jotai";
import {
  FollowUpSchema,
  FollowUpListItemSchema,
  FollowUpWithPatientSchema,
  type FollowUpListItem,
  type FollowUpWithPatient,
  type CreateFollowUpBody,
  type UpdateFollowUpBody,
} from "@/isomorphic/schemas";
import { getAtomStore } from "@lib/atoms";
import { API_ROUTES } from "@/frontend/lib/api/routes";
import { fetchValidated, fetchValidatedArray, postValidated, postValidatedArray } from "@/frontend/lib/validation";
import { accessTokenAtom } from "../core/user";
import {
  _apiPatientFollowUpsAtom,
  _apiWorkspaceFollowUpsAtom,
  patientFollowUpsStatusAtom,
  patientFollowUpsErrorAtom,
  workspaceFollowUpsStatusAtom,
  workspaceFollowUpsErrorAtom,
  currentFollowUpAtom,
  currentFollowUpStatusAtom,
  currentFollowUpErrorAtom,
  currentFollowUpUpdatingAtom,
} from "../core/follow-ups";

// ============================================================================
// FETCH FUNCTIONS
// ============================================================================

/**
 * Fetch all follow-ups for a workspace (for dashboard)
 */
export const fetchWorkspaceFollowUps = async (jwt: string, workspaceId: string): Promise<FollowUpListItem[]> => {
  const store = getAtomStore();

  if (!jwt || !workspaceId) {
    return [];
  }

  store.set(workspaceFollowUpsStatusAtom, "loading");
  store.set(workspaceFollowUpsErrorAtom, null);

  const result = await postValidatedArray(
    FollowUpListItemSchema,
    API_ROUTES.followUps.list,
    { workspaceId },
    {
      jwt,
      context: `Workspace ${workspaceId} Follow-ups`,
    },
  );

  if (result.success) {
    store.set(_apiWorkspaceFollowUpsAtom, result.data);
    store.set(workspaceFollowUpsStatusAtom, "success");
    return result.data;
  } else {
    console.error("[FollowUpActions] Error fetching workspace follow-ups:", result.error);
    store.set(workspaceFollowUpsStatusAtom, "error");
    store.set(workspaceFollowUpsErrorAtom, result.error);
    return [];
  }
};

/**
 * Fetch all follow-ups for a patient
 */
export const fetchPatientFollowUps = async (jwt: string, patientId: string): Promise<void> => {
  const store = getAtomStore();

  if (!jwt || !patientId) {
    return;
  }

  store.set(patientFollowUpsStatusAtom, "loading");
  store.set(patientFollowUpsErrorAtom, null);

  const result = await fetchValidatedArray(FollowUpSchema, API_ROUTES.followUps.getByPatient(patientId), {
    jwt,
    context: `Patient ${patientId} Follow-ups`,
  });

  if (result.success) {
    store.set(_apiPatientFollowUpsAtom, result.data);
    store.set(patientFollowUpsStatusAtom, "success");
  } else {
    console.error("[FollowUpActions] Error fetching patient follow-ups:", result.error);
    store.set(patientFollowUpsStatusAtom, "error");
    store.set(patientFollowUpsErrorAtom, result.error);
  }
};

/**
 * Fetch a single follow-up by ID (with patient data)
 */
export const fetchFollowUp = async (jwt: string, followUpId: string): Promise<FollowUpWithPatient | null> => {
  const store = getAtomStore();
  if (!jwt || !followUpId) {
    return null;
  }
  store.set(currentFollowUpStatusAtom, "loading");
  store.set(currentFollowUpErrorAtom, null);
  const result = await fetchValidated(FollowUpWithPatientSchema, API_ROUTES.followUps.getById(followUpId), {
    jwt,
    context: `Follow-up ${followUpId}`,
  });
  if (result.success) {
    store.set(currentFollowUpAtom, result.data);
    store.set(currentFollowUpStatusAtom, "success");
    return result.data;
  } else {
    console.error("[FollowUpActions] Error fetching follow-up:", result.error);
    store.set(currentFollowUpStatusAtom, "error");
    store.set(currentFollowUpErrorAtom, result.error);
    return null;
  }
};

// ============================================================================
// MUTATION FUNCTIONS
// ============================================================================

/**
 * Create a new follow-up
 * Returns FollowUpListItem (includes patient/agent names from API)
 */
export const createFollowUp = async (jwt: string, payload: CreateFollowUpBody): Promise<FollowUpListItem | null> => {
  const store = getAtomStore();

  if (!jwt) {
    return null;
  }

  const result = await postValidated(FollowUpListItemSchema, API_ROUTES.followUps.create, payload, {
    jwt,
    context: "Create Follow-up",
  });

  if (result.success) {
    // Update workspace follow-ups list (for dashboard)
    // Note: _apiPatientFollowUpsAtom expects FollowUp[], not FollowUpListItem[]
    const workspaceFollowUps = store.get(_apiWorkspaceFollowUpsAtom);
    store.set(_apiWorkspaceFollowUpsAtom, [...workspaceFollowUps, result.data]);
    return result.data;
  } else {
    console.error("[FollowUpActions] Error creating follow-up:", result.error);
    throw new Error(result.error);
  }
};

/**
 * Update an existing follow-up
 */
export const updateFollowUp = async (
  jwt: string,
  followUpId: string,
  payload: Partial<UpdateFollowUpBody>,
): Promise<FollowUpListItem | null> => {
  const store = getAtomStore();

  if (!jwt || !followUpId) {
    return null;
  }

  const result = await postValidated(FollowUpListItemSchema, API_ROUTES.followUps.update(followUpId), payload, {
    jwt,
    context: `Update Follow-up ${followUpId}`,
  });

  if (result.success) {
    // Update workspace follow-ups atom (for dashboard list)
    const currentWorkspaceFollowUps = store.get(_apiWorkspaceFollowUpsAtom);
    store.set(
      _apiWorkspaceFollowUpsAtom,
      currentWorkspaceFollowUps.map((f) => (f.id === followUpId ? result.data : f)),
    );

    // Update patient follow-ups atom (for patient detail view)
    const currentPatientFollowUps = store.get(_apiPatientFollowUpsAtom);
    store.set(
      _apiPatientFollowUpsAtom,
      currentPatientFollowUps.map((f) => (f.id === followUpId ? result.data : f)),
    );

    // Update current follow-up if it's the one being edited
    // Merge to preserve FollowUpWithPatient fields not in FollowUpListItem
    const currentFollowUp = store.get(currentFollowUpAtom);
    if (currentFollowUp?.id === followUpId) {
      store.set(currentFollowUpAtom, { ...currentFollowUp, ...result.data });
    }
    return result.data;
  } else {
    console.error("[FollowUpActions] Error updating follow-up:", result.error);
    throw new Error(result.error);
  }
};

// ============================================================================
// ATOM-BASED ACTIONS (for detail page - reusable across any UI)
// ============================================================================

/**
 * Load follow-up detail by ID
 * Sets currentFollowUpAtom with full FollowUpWithPatient data
 */
export const loadFollowUpDetailAtom = atom(null, async (get, set, followUpId: string) => {
  const jwt = get(accessTokenAtom);
  if (!jwt) {
    console.error("[FollowUpActions] No access token available");
    return;
  }
  if (!followUpId) return;
  set(currentFollowUpStatusAtom, "loading");
  set(currentFollowUpErrorAtom, null);
  const result = await fetchValidated(FollowUpWithPatientSchema, API_ROUTES.followUps.getById(followUpId), {
    jwt,
    context: `Follow-up detail ${followUpId}`,
  });
  if (result.success) {
    set(currentFollowUpAtom, result.data);
    set(currentFollowUpStatusAtom, "success");
    console.log("[FollowUpActions] Follow-up detail loaded:", followUpId);
  } else {
    console.error("[FollowUpActions] Error loading follow-up detail:", result.error);
    set(currentFollowUpStatusAtom, "error");
    set(currentFollowUpErrorAtom, result.error);
  }
});

/**
 * Refresh current follow-up detail (re-fetch from API)
 */
export const refreshFollowUpDetailAtom = atom(null, async (get, set) => {
  const currentFollowUp = get(currentFollowUpAtom);
  if (!currentFollowUp) return;
  const jwt = get(accessTokenAtom);
  if (!jwt) return;
  const result = await fetchValidated(FollowUpWithPatientSchema, API_ROUTES.followUps.getById(currentFollowUp.id), {
    jwt,
    context: `Refresh follow-up ${currentFollowUp.id}`,
  });
  if (result.success) {
    set(currentFollowUpAtom, result.data);
    console.log("[FollowUpActions] Follow-up detail refreshed:", currentFollowUp.id);
  } else {
    console.error("[FollowUpActions] Error refreshing follow-up:", result.error);
  }
});

/**
 * Toggle pause/resume for the current follow-up
 * Optimistic update + API call + refresh
 */
export const togglePauseFollowUpAtom = atom(null, async (get, set) => {
  const followUp = get(currentFollowUpAtom);
  if (!followUp) return;
  const jwt = get(accessTokenAtom);
  if (!jwt) return;
  const newStatus = followUp.status === "active" ? "paused" : "active";
  // Optimistic update
  set(currentFollowUpUpdatingAtom, true);
  set(currentFollowUpAtom, { ...followUp, status: newStatus });
  try {
    await postValidated(
      FollowUpListItemSchema,
      API_ROUTES.followUps.update(followUp.id),
      { status: newStatus },
      { jwt, context: `Toggle pause follow-up ${followUp.id}` },
    );
    // Re-fetch full data to ensure consistency
    const refreshResult = await fetchValidated(FollowUpWithPatientSchema, API_ROUTES.followUps.getById(followUp.id), {
      jwt,
      context: `Refresh after toggle ${followUp.id}`,
    });
    if (refreshResult.success) {
      set(currentFollowUpAtom, refreshResult.data);
    }
    // Update workspace list atom too
    const workspaceFollowUps = getAtomStore().get(_apiWorkspaceFollowUpsAtom);
    if (workspaceFollowUps.length > 0) {
      set(
        _apiWorkspaceFollowUpsAtom,
        workspaceFollowUps.map((f) => (f.id === followUp.id ? { ...f, status: newStatus } : f)),
      );
    }
    console.log("[FollowUpActions] Follow-up toggled:", followUp.id, newStatus);
    return newStatus;
  } catch (err) {
    // Rollback optimistic update
    set(currentFollowUpAtom, followUp);
    console.error("[FollowUpActions] Error toggling follow-up:", err);
    throw err;
  } finally {
    set(currentFollowUpUpdatingAtom, false);
  }
});

/**
 * Cancel the current follow-up with a reason
 * Optimistic update + API call + refresh
 */
export const cancelFollowUpAtom = atom(null, async (get, set, reason: string) => {
  const followUp = get(currentFollowUpAtom);
  if (!followUp || !reason.trim()) return;
  const jwt = get(accessTokenAtom);
  if (!jwt) return;
  // Optimistic update
  set(currentFollowUpUpdatingAtom, true);
  set(currentFollowUpAtom, { ...followUp, status: "cancelled", cancellationReason: reason });
  try {
    await postValidated(
      FollowUpListItemSchema,
      API_ROUTES.followUps.update(followUp.id),
      { status: "cancelled", cancellationReason: reason },
      { jwt, context: `Cancel follow-up ${followUp.id}` },
    );
    // Re-fetch full data
    const refreshResult = await fetchValidated(FollowUpWithPatientSchema, API_ROUTES.followUps.getById(followUp.id), {
      jwt,
      context: `Refresh after cancel ${followUp.id}`,
    });
    if (refreshResult.success) {
      set(currentFollowUpAtom, refreshResult.data);
    }
    // Update workspace list atom
    const workspaceFollowUps = getAtomStore().get(_apiWorkspaceFollowUpsAtom);
    if (workspaceFollowUps.length > 0) {
      set(
        _apiWorkspaceFollowUpsAtom,
        workspaceFollowUps.map((f) =>
          f.id === followUp.id ? { ...f, status: "cancelled" as const, cancellationReason: reason } : f,
        ),
      );
    }
    console.log("[FollowUpActions] Follow-up cancelled:", followUp.id);
  } catch (err) {
    // Rollback optimistic update
    set(currentFollowUpAtom, followUp);
    console.error("[FollowUpActions] Error cancelling follow-up:", err);
    throw err;
  } finally {
    set(currentFollowUpUpdatingAtom, false);
  }
});

/**
 * Update current follow-up settings (metadata, authConfig, etc.)
 * Used by settings tab - updates atom + API, then refreshes
 */
export const updateFollowUpSettingsAtom = atom(null, async (get, set, payload: Partial<UpdateFollowUpBody>) => {
  const followUp = get(currentFollowUpAtom);
  if (!followUp) return;
  const jwt = get(accessTokenAtom);
  if (!jwt) return;
  set(currentFollowUpUpdatingAtom, true);
  try {
    await postValidated(FollowUpListItemSchema, API_ROUTES.followUps.update(followUp.id), payload, {
      jwt,
      context: `Update follow-up settings ${followUp.id}`,
    });
    // Re-fetch full data to get merged result
    const refreshResult = await fetchValidated(FollowUpWithPatientSchema, API_ROUTES.followUps.getById(followUp.id), {
      jwt,
      context: `Refresh after settings update ${followUp.id}`,
    });
    if (refreshResult.success) {
      set(currentFollowUpAtom, refreshResult.data);
    }
    console.log("[FollowUpActions] Follow-up settings updated:", followUp.id);
  } catch (err) {
    console.error("[FollowUpActions] Error updating follow-up settings:", err);
    throw err;
  } finally {
    set(currentFollowUpUpdatingAtom, false);
  }
});

/**
 * Clear current follow-up detail (on unmount / navigation away)
 */
export const clearFollowUpDetailAtom = atom(null, (_get, set) => {
  set(currentFollowUpAtom, null);
  set(currentFollowUpStatusAtom, "init");
  set(currentFollowUpErrorAtom, null);
  set(currentFollowUpUpdatingAtom, false);
});
